import QRCode from 'qrcode';
import { captureFormDraft, restoreFormDraft } from './form-drafts.js';
import { attachSketchInput } from './sketch-input.js';
import { SIGNAL_TILES } from '../game-data.js';
import { audio } from './audio.js';
import { PartyVisuals } from './visuals.js';
import './styles.css';
import '../../../../hub/shared/brand.css';
import { mountBrand } from '../../../../hub/shared/brand.mjs';
import { shareResult } from '../../../../hub/shared/share.mjs';
mountBrand({ daily: false });

const GAMES = {
  'signal-jam': {
    name: 'SIGNAL JAM',
    icon: '◉',
    accent: '#35b8ff',
    kicker: 'Three clues. One ridiculous scene.',
    description: 'Build a clear three-tile clue from recognizable sounds and icons. The room races to match it to the right ridiculous scenario.',
    detail: '3–8 rounds · 2–8 players · easy to learn',
  },
  'story-circuit': {
    name: 'STORY CIRCUIT',
    icon: '✦',
    accent: '#ff4fc8',
    kicker: 'Secret-direction storytelling',
    description: 'Write the next beat under a private creative directive, decode everyone else’s mission, and vote one twist into canon.',
    detail: '3–8 rounds · 2–8 players · narrated reveals',
  },
  'vector-vault': {
    name: 'VECTOR VAULT',
    icon: '⌁',
    accent: '#69f5b1',
    kicker: 'Cooperative gravity control',
    description: 'Every phone controls a quirky thruster. Coordinate direction and power to steer the core through a gorgeous cosmic obstacle course.',
    detail: '3–8 rounds · 2–8 players · team physics',
  },
  'crowd-current': {
    name: 'CROWD CURRENT',
    icon: '≈',
    accent: '#ffb44a',
    kicker: 'Find out how your friends think',
    description: 'Map your honest take on a two-axis field while the forecaster predicts the room’s center. Reveal the constellation and crown its best broadcast.',
    detail: '3–8 rounds · 2–8 players · anonymous reveals',
  },
  'clue-collision': {
    name: 'CLUE COLLISION',
    icon: '✹',
    accent: '#efdd68',
    kicker: 'One-word teamwork with a trap',
    description: 'Help the rotating solver guess a secret word—but matching clues collide and disappear before the reveal.',
    detail: '3–8 rounds · 2–8 players · instant rules',
  },
  'vibe-check': {
    name: 'VIBE CHECK',
    icon: '♡',
    accent: '#ff7f9f',
    kicker: 'How well do you know the room?',
    description: 'The hot-seat player secretly picks a favorite and a hard no. Everyone else tries to read both sides of their personality.',
    detail: '3–8 rounds · 2–8 players · personal and social',
  },
  'bluff-bureau': {
    name: 'BLUFF BUREAU',
    icon: '⌕',
    accent: '#5de2d7',
    kicker: 'Believable lies, surprising truths',
    description: 'File a fake answer to a strange fact, then find the truth hiding among the room’s most convincing bluffs.',
    detail: '3–8 rounds · 2–8 players · anonymous deception',
  },
  'sketch-switch': {
    name: 'SKETCH SWITCH',
    icon: '▦',
    accent: '#c58cff',
    kicker: 'Draw it. Pass it. Misread it.',
    description: 'Draw a secret prompt, caption somebody else’s mystery sketch, then vote for the funniest accidental collaboration.',
    detail: '3–8 rounds · 2–8 players · phone drawing',
  },
};

const SIGNAL_TILE_MAP = Object.fromEntries(SIGNAL_TILES.map((tile) => [tile.id, tile]));

const app = document.getElementById('app');
const toast = document.getElementById('toast');
const visuals = new PartyVisuals(document.getElementById('ambient'));
const params = new URLSearchParams(location.search);
let session = null;
let state = null;
let socket = null;
let reconnectTimer = null;
let reconnectAttempt = 0;
let selectedLandingGame = GAMES[params.get('game')] ? params.get('game') : 'signal-jam';
let signalDraft = [];
let vectorDraft = { angle: 315, power: 70 };
let crowdDraft = { x: 50, y: 50, confidence: 60, label: '' };
let vibeDraft = { favorite: '', nope: '' };
let sketchDraft = '0'.repeat(192);
let sketchColor = '1';
let textDrafts = { collisionClue: '', collisionGuess: '', bluff: '', sketchCaption: '' };
let lastStateKey = '';
let renderedStateKey = '';
let formDraft = null;
let timerInterval = null;

const sketchInput = attachSketchInput(app, paintSketchCell, () => renderGame());

boot();

function boot() {
  const room = normalizeRoom(params.get('room'));
  if (!room) {
    renderLanding();
    return;
  }
  const stored = readSession(room);
  if (stored && (!params.has('host') || stored.role === 'host')) {
    session = stored;
    connect();
  } else {
    renderJoin(room);
  }
}

function renderLanding() {
  document.body.className = 'is-landing';
  app.innerHTML = `
    ${brandHeader()}
    <main class="landing shell">
      <section class="hero">
        <span class="eyebrow">JOYBOX PARTY</span>
        <h1>Your room.<br><span>Your phones.</span><br>Game on.</h1>
        <p>Put this screen on the TV, pick a game, and share one room code. No app or account needed.</p>
        <div class="hero__actions">
          <button class="button button--primary" data-action="create-room">Start a party</button>
          <button class="button button--ghost" data-action="focus-join">Join with a code</button>
        </div>
      </section>
      <section class="game-picker" aria-labelledby="games-title">
        <div class="section-heading">
          <div><span class="eyebrow">EIGHT WAYS TO PLAY</span><h2 id="games-title">Choose your chaos</h2></div>
          <span class="player-pill">2–8 players</span>
        </div>
        <div class="game-cards">${Object.entries(GAMES).map(([id, game]) => gameCard(id, game, selectedLandingGame === id)).join('')}</div>
        ${gameRules(selectedLandingGame)}
      </section>
      <section class="join-panel" id="join-panel">
        <div><span class="eyebrow">HAVE A CODE?</span><h2>Join the room</h2><p>Use the six-character code on the host screen.</p></div>
        <form id="landing-join" class="join-inline">
          <label class="sr-only" for="room-code">Room code</label>
          <input id="room-code" name="room" class="code-input" minlength="6" maxlength="6" pattern="[A-Z2-9]{6}" autocomplete="off" autocapitalize="characters" placeholder="ROOM CODE" required>
          <button class="button button--secondary">Join</button>
        </form>
      </section>
      <section class="how-it-works">
        <span><b>1</b> Pick a game</span><i></i><span><b>2</b> Friends join by phone</span><i></i><span><b>3</b> Play together</span>
      </section>
    </main>
    ${footer()}
  `;
}

function renderJoin(room, error = '') {
  document.body.className = 'is-player is-joining';
  app.innerHTML = `
    ${brandHeader(false)}
    <main class="join-screen shell shell--narrow">
      <section class="join-card glass-card">
        <span class="eyebrow">JOINING ROOM</span>
        <div class="room-code room-code--small">${escapeHtml(room)}</div>
        <h1>What should we call you?</h1>
        <form id="player-join" data-room="${escapeHtml(room)}">
          <label for="player-name">Display name</label>
          <input id="player-name" name="name" maxlength="18" autocomplete="nickname" placeholder="Your name" required autofocus>
          <p class="form-error" role="alert">${escapeHtml(error)}</p>
          <button class="button button--primary button--wide">Enter the party</button>
        </form>
      </section>
    </main>
  `;
}

function renderConnecting() {
  if (app.querySelector('form')) formDraft = captureFormDraft(app);
  document.body.className = session?.role === 'host' ? 'is-host' : 'is-player';
  app.innerHTML = `
    ${brandHeader(false)}
    <main class="shell shell--center"><div class="connection-card"><div class="spinner"></div><h1>Connecting to ${escapeHtml(session?.code || '')}</h1><p>Finding the party…</p></div></main>
  `;
}

function renderGame() {
  if (!state || !session) return;
  const renderKey = `${state.selectedGame}:${state.phase}:${state.round}`;
  // Keep a touch stroke intact during same-phase progress broadcasts.
  if (sketchInput.active && renderedStateKey === renderKey) return;
  sketchInput.cancel();
  if (renderedStateKey !== renderKey) formDraft = null;
  else if (app.querySelector('form')) formDraft = captureFormDraft(app);
  renderedStateKey = renderKey;
  document.body.className = session.role === 'host' ? 'is-host' : 'is-player';
  visuals.setSignal(state.phase === 'signal-guess' || state.results?.kind === 'signal' ? state.signal?.clue || state.results?.clue : null);
  app.innerHTML = `
    ${gameHeader()}
    <main class="game-shell ${session.role === 'host' ? 'game-shell--host' : 'game-shell--phone'}">
      ${state.notice ? `<p class="room-notice" role="status">${escapeHtml(state.notice)}</p>` : ''}
      ${session.role === 'host' ? renderHost() : renderPlayer()}
    </main>
    ${session.role === 'player' ? reactionBar() : ''}
  `;
  restoreFormDraft(app, formDraft);
  afterRender();
}

function renderHost() {
  const game = GAMES[state.selectedGame];
  if (state.phase === 'lobby') {
    return `
      <section class="host-lobby">
        <div class="lobby-main glass-card">
          <div class="room-invite">
            <div><span class="eyebrow">JOIN AT PARTY.JOYBOXLABS.COM</span><div class="room-code">${escapeHtml(state.code)}</div></div>
            <canvas id="room-qr" width="164" height="164" aria-label="QR code to join room ${escapeHtml(state.code)}"></canvas>
          </div>
          <div class="host-game-select">
            <span class="eyebrow">SELECTED GAME</span>
            <div class="mini-games">${Object.entries(GAMES).map(([id, item]) => miniGame(id, item, id === state.selectedGame)).join('')}</div>
          </div>
          <button class="button button--primary button--xl" data-action="start-game" ${state.players.length < 2 ? 'disabled' : ''}>
            ${state.players.length < 2 ? 'Waiting for players…' : `Start ${escapeHtml(game.name)}`}
          </button>
          <p class="lobby-hint">${state.players.length}/8 joined · At least 2 players needed</p><p class="lobby-hint">This is the shared host screen. Want to play too? Join with your phone, just like everyone else.</p>
          ${gameRules(state.selectedGame)}
        </div>
        ${playerLobby()}
      </section>`;
  }

  if (state.phase === 'game-over') return renderGameOver();

  return `
    <section class="host-stage">
      <div class="stage-main glass-card">
        ${roundBar()}
        ${renderSelectedHost()}
      </div>
      ${scoreboard()}
    </section>`;
}

function renderSelectedHost() {
  if (state.selectedGame === 'signal-jam') return renderSignalHost();
  if (state.selectedGame === 'story-circuit') return renderStoryHost();
  if (state.selectedGame === 'vector-vault') return renderVectorHost();
  if (state.selectedGame === 'crowd-current') return renderCrowdHost();
  if (state.selectedGame === 'clue-collision') return renderCollisionHost();
  if (state.selectedGame === 'vibe-check') return renderVibeHost();
  if (state.selectedGame === 'bluff-bureau') return renderBluffHost();
  return renderSketchHost();
}

function renderSignalHost() {
  const conductor = playerById(state.signal?.conductorId);
  if (state.phase === 'signal-compose') {
    return waitingPanel(
      `${conductor?.avatar || '◉'} ${escapeHtml(conductor?.name || 'The conductor')} is building a signal`,
      'Everyone else: study the screen, but no peeking at their phone.',
    );
  }
  if (state.phase === 'signal-guess') {
    const tiles = state.signal.clue?.tiles || [];
    return `
      <div class="signal-stage">
        <span class="eyebrow">DECODE THE SIGNAL</span>
        <h1>${escapeHtml(conductor?.name || 'The conductor')} sent three clues.</h1>
        <div class="signal-sequence">${tiles.map((id, index) => signalTile(id, index + 1, false)).join('')}</div>
        <p>Read the icons, listen to the sounds, then choose the matching scenario on your phone.</p>
        <button class="button button--secondary" data-action="play-signal">▶ Replay all three cues</button>
        ${progressMeter()}
      </div>`;
  }
  return renderRoundResults();
}

function renderStoryHost() {
  if (state.phase === 'story-write') {
    return waitingPanel('The story room is writing', escapeHtml(state.story.setup));
  }
  if (state.phase === 'story-vote') {
    return `
      <div class="story-stage">
        <span class="eyebrow">THE CIRCUIT IS LIVE</span>
        <h1>${escapeHtml(state.story.setup)}</h1>
        <div class="story-cards host-story-cards">${state.story.submissions.map((entry, index) => `
          <article class="story-card"><span>BEAT ${index + 1}</span><p>${escapeHtml(entry.text)}</p></article>`).join('')}</div>
        <button class="button button--secondary" data-action="narrate-stories">🔊 Narrate every beat</button>
        ${progressMeter()}
      </div>`;
  }
  return renderRoundResults();
}

function renderVectorHost() {
  if (state.phase === 'vector-command') {
    return `
      <div class="vector-stage">
        <span class="eyebrow">CREW MANEUVER</span>
        <h1>${escapeHtml(state.vector.course.scenario)}</h1>
        <p>${escapeHtml(state.vector.course.objective)}</p>
        ${vectorMap(state.vector.course)}
        <div class="turbulence-readout"><span>↝</span><b>Turbulence</b><small>${state.vector.course.turbulence[1]} drift units at ${state.vector.course.turbulence[0]}°</small></div>
        ${progressMeter()}
        <button class="text-button" data-action="finish-phase">Fire with current commands</button>
      </div>`;
  }
  return renderRoundResults();
}

function renderCrowdHost() {
  const forecaster = playerById(state.crowd?.forecasterId);
  if (state.phase === 'crowd-place') {
    return `
      <div class="crowd-stage">
        <span class="eyebrow">READ THE ROOM</span>
        <h1>${escapeHtml(state.crowd.prompt.question)}</h1>
        <p>${forecaster?.avatar || '◎'} ${escapeHtml(forecaster?.name || 'The forecaster')} is predicting where the crowd will gather.</p>
        ${crowdMap(state.crowd.prompt, [], null, null)}
        ${progressMeter()}
        <button class="text-button" data-action="finish-phase">Reveal the current now</button>
      </div>`;
  }
  if (state.phase === 'crowd-vote') {
    return `
      <div class="crowd-stage">
        <span class="eyebrow">THE CURRENT APPEARS</span>
        <h1>${escapeHtml(state.crowd.prompt.question)}</h1>
        ${crowdMap(state.crowd.prompt, state.crowd.pulses, state.crowd.prediction, null)}
        <div class="broadcast-strip">${state.crowd.pulses.map((pulse) => `<span>“${escapeHtml(pulse.label)}”</span>`).join('')}</div>
        ${progressMeter()}
      </div>`;
  }
  return renderRoundResults();
}

function renderCollisionHost() {
  const solver = playerById(state.collision?.solverId);
  if (state.phase === 'collision-write') {
    return waitingPanel(
      `Everyone is helping ${solver?.avatar || '✹'} ${escapeHtml(solver?.name || 'the solver')}`,
      `Category: ${escapeHtml(state.collision.category)}. Matching one-word clues will collide and vanish.`,
    );
  }
  if (state.phase === 'collision-guess') {
    return `<div class="collision-stage">
      <span class="eyebrow">SURVIVING CLUES · ${escapeHtml(state.collision.category)}</span>
      <h1>${escapeHtml(solver?.name || 'The solver')}, say the secret word.</h1>
      <div class="clue-cloud">${state.collision.clues.length ? state.collision.clues.map((clue) => `<span>${escapeHtml(clue.text)}</span>`).join('') : '<em>Every clue collided. Trust the category!</em>'}</div>
      ${progressMeter()}
      <button class="text-button" data-action="finish-phase">Reveal the word</button>
    </div>`;
  }
  return renderRoundResults();
}

function renderVibeHost() {
  const subject = playerById(state.vibe?.subjectId);
  if (state.phase === 'vibe-pick') {
    return `<div class="vibe-stage">
      <span class="eyebrow">READ ${escapeHtml(subject?.name || 'THE HOT SEAT')}</span>
      <h1>${escapeHtml(state.vibe.prompt.question)}</h1>
      <div class="vibe-options">${state.vibe.prompt.options.map((option, index) => `<span><i>${index + 1}</i>${escapeHtml(option)}</span>`).join('')}</div>
      <p>${escapeHtml(subject?.name || 'The hot-seat player')} is secretly choosing a favorite and a hard no. Everyone else predicts both.</p>
      ${progressMeter()}
      <button class="text-button" data-action="finish-phase">Reveal the vibe</button>
    </div>`;
  }
  return renderRoundResults();
}

function renderBluffHost() {
  if (state.phase === 'bluff-write') {
    return waitingPanel('The Bureau is collecting believable lies', escapeHtml(state.bluff.question));
  }
  if (state.phase === 'bluff-vote') {
    return `<div class="bluff-stage">
      <span class="eyebrow">ONE ANSWER IS TRUE</span>
      <h1>${escapeHtml(state.bluff.question)}</h1>
      <div class="bluff-board">${state.bluff.options.map((option) => `<article><span>FILE ${option.id.slice(-4).toUpperCase()}</span><b>${escapeHtml(option.text)}</b></article>`).join('')}</div>
      ${progressMeter()}
      <button class="text-button" data-action="finish-phase">Close the case</button>
    </div>`;
  }
  return renderRoundResults();
}

function renderSketchHost() {
  if (state.phase === 'sketch-draw') {
    return waitingPanel('Every phone is now a tiny art studio', 'Draw the private prompt. Clarity is optional; commitment is mandatory.');
  }
  if (state.phase === 'sketch-caption') {
    return waitingPanel('The sketches have switched phones', 'Everyone is naming somebody else’s drawing without seeing the original prompt.');
  }
  if (state.phase === 'sketch-vote') {
    return `<div class="sketch-stage">
      <span class="eyebrow">THE ACCIDENTAL GALLERY</span>
      <h1>Best drawing-and-caption combo?</h1>
      <div class="sketch-gallery">${state.sketch.pairs.map((pair) => `<article>${sketchGrid(pair.cells)}<p>“${escapeHtml(pair.caption)}”</p></article>`).join('')}</div>
      ${progressMeter()}
      <button class="text-button" data-action="finish-phase">Close gallery voting</button>
    </div>`;
  }
  return renderRoundResults();
}

function vectorMap(course, result = null) {
  const end = result?.end;
  return `<div class="vector-map ${end ? 'is-resolved' : ''}" role="img" aria-label="Space course with target and hazards">
    <div class="star-field"><i></i><i></i><i></i><i></i><i></i></div>
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <defs><filter id="core-glow"><feGaussianBlur stdDeviation="2.5" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
      ${course.hazards.map((hazard) => `<g class="space-hazard"><circle cx="${safeNumber(hazard.x)}" cy="${safeNumber(hazard.y)}" r="${safeNumber(hazard.r)}"/><circle cx="${safeNumber(hazard.x)}" cy="${safeNumber(hazard.y)}" r="${safeNumber(hazard.r * 0.45)}"/></g>`).join('')}
      <circle class="vault-ring" cx="${safeNumber(course.target[0])}" cy="${safeNumber(course.target[1])}" r="${safeNumber(course.radius)}"/>
      <circle class="vault-center" cx="${safeNumber(course.target[0])}" cy="${safeNumber(course.target[1])}" r="3"/>
      ${end ? `<line class="flight-path ${result.collision ? 'is-collision' : ''}" x1="${safeNumber(course.start[0])}" y1="${safeNumber(course.start[1])}" x2="${safeNumber(end[0])}" y2="${safeNumber(end[1])}"/>` : ''}
      <circle class="energy-core" filter="url(#core-glow)" cx="${safeNumber(end?.[0] ?? course.start[0])}" cy="${safeNumber(end?.[1] ?? course.start[1])}" r="4"/>
    </svg>
    <span class="map-label map-label--start" style="--x:${safeNumber(course.start[0])}%;--y:${safeNumber(course.start[1])}%">CORE</span>
    <span class="map-label map-label--target" style="--x:${safeNumber(course.target[0])}%;--y:${safeNumber(course.target[1])}%">VAULT</span>
  </div>`;
}

function crowdMap(prompt, pulses = [], prediction = null, centroid = null, revealAuthors = false) {
  return `<div class="crowd-map" role="img" aria-label="Two-axis opinion map">
    <div class="crowd-grid"></div>
    <span class="axis-label axis-label--left">${escapeHtml(prompt.xLow)}</span>
    <span class="axis-label axis-label--right">${escapeHtml(prompt.xHigh)}</span>
    <span class="axis-label axis-label--top">${escapeHtml(prompt.yHigh)}</span>
    <span class="axis-label axis-label--bottom">${escapeHtml(prompt.yLow)}</span>
    ${pulses.map((pulse, index) => {
      const author = revealAuthors ? playerById(pulse.authorId) : null;
      return `<i class="crowd-point" style="--x:${safeNumber(pulse.x)}%;--y:${safeNumber(pulse.y)}%;--delay:${index * 80}ms" title="${escapeAttr(pulse.label)}"><b>${author?.avatar || '•'}</b></i>`;
    }).join('')}
    ${prediction ? `<i class="crowd-prediction" style="--x:${safeNumber(prediction.x)}%;--y:${safeNumber(prediction.y)}%" title="Forecaster prediction">★</i>` : ''}
    ${centroid ? `<i class="crowd-centroid" style="--x:${safeNumber(centroid.x)}%;--y:${safeNumber(centroid.y)}%" title="Crowd center">◎</i>` : ''}
  </div>`;
}

function renderRoundResults() {
  if (state.results?.kind === 'signal') {
    const result = state.results;
    const conductor = playerById(result.conductorId);
    return `
      <div class="results-panel">
        <span class="eyebrow">SIGNAL DECODED</span>
        <h1>${escapeHtml(result.answer)}</h1>
        <p>${escapeHtml(conductor?.name || 'The conductor')} earned ${result.conductorPoints.toLocaleString()} conductor points.</p>
        <div class="result-list">${result.guesses.map((guess) => {
          const player = playerById(guess.playerId);
          return `<div class="result-row ${guess.correct ? 'is-correct' : ''}"><span>${player?.avatar || ''} ${escapeHtml(player?.name || '')}</span><b>${guess.correct ? `+${guess.points}` : 'miss'}</b></div>`;
        }).join('')}</div>
        ${nextButton()}
      </div>`;
  }
  if (state.results?.kind === 'vector') {
    const result = state.results;
    return `
      <div class="results-panel vector-results">
        <span class="eyebrow">MANEUVER COMPLETE</span>
        <h1>${result.landed ? 'Vault cracked!' : result.collision ? 'Hazard clipped!' : 'So close—the core drifted.'}</h1>
        <p>${result.landed ? 'The crew landed inside the target field.' : `The core stopped ${result.distance} units from center.`}</p>
        ${vectorMap(result.course, result)}
        <div class="vector-score-callout"><b>+${result.teamPoints.toLocaleString()}</b><span>crew precision</span></div>
        <div class="thruster-results">${result.commands.map((command) => {
          const player = playerById(command.playerId);
          const isAce = result.aceIds.includes(command.playerId);
          return `<div class="thruster-result ${isAce ? 'is-ace' : ''}"><span>${player?.avatar || ''}</span><div><b>${escapeHtml(player?.name || '')}${isAce ? ' · ACE' : ''}</b><small>${Math.round(command.angle)}° at ${Math.round(command.power)}% → effective ${Math.round(command.effectiveAngle)}°</small></div><strong>+${result.playerPoints.find((entry) => entry.playerId === command.playerId)?.points || 0}</strong></div>`;
        }).join('')}</div>
        ${nextButton()}
      </div>`;
  }
  if (state.results?.kind === 'crowd') {
    const result = state.results;
    const forecaster = playerById(result.forecasterId);
    const sorted = [...result.pulses].sort((a, b) => b.sparkVotes - a.sparkVotes || b.points - a.points);
    return `
      <div class="results-panel crowd-results">
        <span class="eyebrow">CURRENT MAPPED</span>
        <h1>${escapeHtml(forecaster?.name || 'The forecaster')} missed by ${result.error}.</h1>
        <p>★ forecast · ◎ actual crowd center · +${result.forecastPoints.toLocaleString()} forecast points</p>
        ${crowdMap(result.prompt, result.pulses, result.prediction, result.centroid, true)}
        <div class="crowd-reveal-list">${sorted.map((pulse, index) => {
          const player = playerById(pulse.authorId);
          return `<div class="crowd-reveal ${index === 0 && pulse.sparkVotes ? 'is-spark' : ''}"><span>${player?.avatar || '•'}</span><div><b>“${escapeHtml(pulse.label)}”</b><small>${escapeHtml(player?.name || '')} · ${pulse.sparkVotes} spark vote${pulse.sparkVotes === 1 ? '' : 's'}${pulse.liveWire ? ' · LIVE WIRE' : ''}</small></div><strong>+${pulse.points}</strong></div>`;
        }).join('')}</div>
        ${nextButton()}
      </div>`;
  }
  if (state.results?.kind === 'collision') {
    const result = state.results;
    const solver = playerById(result.solverId);
    return `<div class="results-panel collision-results">
      <span class="eyebrow">${result.correct ? 'DIRECT HIT' : 'SECRET REVEALED'}</span>
      <h1>${escapeHtml(result.target.word)}</h1>
      <p>${escapeHtml(solver?.name || 'The solver')} guessed ${result.guess ? `“${escapeHtml(result.guess)}”` : 'nothing'} · +${result.solverPoints}</p>
      <div class="collision-results__clues">${result.clues.map((clue) => {
        const player = playerById(clue.authorId);
        return `<div class="${clue.survived ? 'is-safe' : 'is-collision'}"><span>${clue.survived ? '✓' : '✕'}</span><b>${escapeHtml(clue.text)}</b><small>${escapeHtml(player?.name || '')} · +${clue.points}</small></div>`;
      }).join('')}</div>
      ${nextButton()}
    </div>`;
  }
  if (state.results?.kind === 'vibe') {
    const result = state.results;
    const subject = playerById(result.subjectId);
    return `<div class="results-panel vibe-results">
      <span class="eyebrow">${escapeHtml(subject?.name || 'HOT SEAT')} REVEALED</span>
      <h1>${escapeHtml(result.choice.favorite)}</h1>
      <p>Favorite above · hard no: <b>${escapeHtml(result.choice.nope)}</b></p>
      <div class="vibe-predictions">${result.predictions.map((prediction) => {
        const player = playerById(prediction.playerId);
        return `<div><span>${player?.avatar || ''}</span><b>${escapeHtml(player?.name || '')}</b><small>${prediction.favoriteCorrect ? '✓ favorite' : '✕ favorite'} · ${prediction.nopeCorrect ? '✓ nope' : '✕ nope'}</small><strong>+${prediction.points}</strong></div>`;
      }).join('')}</div>
      <small class="subject-score">The hot seat earned +${result.subjectPoints} for being readable.</small>
      ${nextButton()}
    </div>`;
  }
  if (state.results?.kind === 'bluff') {
    const result = state.results;
    const sorted = [...result.options].sort((a, b) => Number(b.truth) - Number(a.truth) || b.fooled - a.fooled);
    return `<div class="results-panel bluff-results">
      <span class="eyebrow">CASE CLOSED</span>
      <h1>${escapeHtml(result.question.answer)}</h1>
      <p>The strange truth was hiding in plain sight.</p>
      <div class="bluff-reveals">${sorted.map((option) => `<article class="${option.truth ? 'is-truth' : ''}"><span>${option.truth ? 'TRUE FILE' : `${option.fooled} FOOLED`}</span><b>${escapeHtml(option.text)}</b><small>${option.authorIds.length ? option.authorIds.map((id) => escapeHtml(playerById(id)?.name || '')).join(' + ') : option.truth ? 'Official answer' : 'House bluff'}${option.authorPoints ? ` · +${option.authorPoints}` : ''}</small></article>`).join('')}</div>
      ${result.truthTellers.length ? `<p class="truth-tellers">Already knew it: ${result.truthTellers.map((id) => escapeHtml(playerById(id)?.name || '')).join(', ')} · +750</p>` : ''}
      ${nextButton()}
    </div>`;
  }
  if (state.results?.kind === 'sketch') {
    const sorted = [...state.results.pairs].sort((a, b) => b.votes - a.votes);
    return `<div class="results-panel sketch-results">
      <span class="eyebrow">GALLERY REVEAL</span>
      <h1>${sorted[0]?.votes ? 'The room found its masterpiece.' : 'Every masterpiece is a mystery.'}</h1>
      <div class="sketch-gallery sketch-gallery--results">${sorted.map((pair, index) => {
        const artist = playerById(pair.artistId);
        const captioner = playerById(pair.captionerId);
        return `<article class="${index === 0 && pair.votes ? 'is-winner' : ''}">${sketchGrid(pair.cells)}<span>ORIGINAL: ${escapeHtml(pair.prompt)}</span><p>“${escapeHtml(pair.caption)}”</p><small>${artist?.avatar || ''} ${escapeHtml(artist?.name || '')} drew · ${captioner?.avatar || ''} ${escapeHtml(captioner?.name || '')} captioned · ${pair.votes} vote${pair.votes === 1 ? '' : 's'} · +${pair.points} each</small></article>`;
      }).join('')}</div>
      ${nextButton()}
    </div>`;
  }
  const sorted = [...(state.results?.submissions || [])].sort((a, b) => b.points - a.points);
  return `
    <div class="results-panel">
      <span class="eyebrow">CANON REVEAL</span>
      <h1>The room has spoken.</h1>
      <div class="story-results">${sorted.map((entry, index) => {
        const author = playerById(entry.authorId);
        return `<article class="story-result ${index === 0 ? 'is-canon' : ''}">
          <div><span>${index === 0 ? '★ CANON' : `BEAT ${index + 1}`}</span><b>${author?.avatar || ''} ${escapeHtml(author?.name || '')}</b></div>
          <p>${escapeHtml(entry.text)}</p>
          <small>${entry.canonVotes} canon vote${entry.canonVotes === 1 ? '' : 's'} · mission decoded ${entry.decodedBy}× · +${entry.points}</small>
          <em>${escapeHtml(entry.direction)}</em>
        </article>`;
      }).join('')}</div>
      <button class="button button--secondary" data-action="narrate-results">🔊 Narrate the winner</button>
      ${nextButton()}
    </div>`;
}

function renderGameOver() {
  const winners = state.winnerIds.map(playerById).filter(Boolean);
  return `
    <section class="game-over glass-card">
      <span class="eyebrow">FINAL SCORE</span>
      <div class="trophy">✦</div>
      <h1>${winners.length > 1 ? 'A legendary tie!' : `${escapeHtml(winners[0]?.name || 'Winner')} wins!`}</h1>
      <p>${winners.map((player) => `${player.avatar} ${escapeHtml(player.name)}`).join(' & ')}</p>
      ${scoreboard(true)}
      <button class="button button--secondary" data-action="share-result">Share party result ↗</button>
      <button class="button button--primary button--xl" data-action="return-lobby">Play another game</button>
    </section>`;
}

function renderPlayer() {
  const me = playerById(session.id);
  if (!me) return `<section class="phone-card glass-card"><h1>Session ended</h1><button class="button button--primary" data-action="forget-session">Join again</button></section>`;
  if (state.phase === 'lobby') {
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${me.avatar}</div><span class="eyebrow">YOU'RE IN</span><h1>${escapeHtml(me.name)}</h1><p>The host is choosing a game. Keep this screen open.</p>${smallPlayers()}</section>`;
  }
  if (state.phase === 'game-over') {
    const won = state.winnerIds.includes(me.id);
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${won ? '🏆' : me.avatar}</div><span class="eyebrow">FINAL SCORE</span><h1>${won ? 'You won!' : `${me.score.toLocaleString()} points`}</h1><p>${won ? `${me.score.toLocaleString()} points. Absolute party legend.` : 'The host can start another game.'}</p>${smallScoreboard()}<button class="button button--primary" data-action="share-result">Share my score ↗</button></section>`;
  }
  if (state.selectedGame === 'signal-jam') return renderSignalPlayer(me);
  if (state.selectedGame === 'story-circuit') return renderStoryPlayer(me);
  if (state.selectedGame === 'vector-vault') return renderVectorPlayer(me);
  if (state.selectedGame === 'crowd-current') return renderCrowdPlayer(me);
  if (state.selectedGame === 'clue-collision') return renderCollisionPlayer(me);
  if (state.selectedGame === 'vibe-check') return renderVibePlayer(me);
  if (state.selectedGame === 'bluff-bureau') return renderBluffPlayer(me);
  return renderSketchPlayer(me);
}

function renderSignalPlayer(me) {
  const isConductor = state.signal?.conductorId === me.id;
  if (state.phase === 'signal-compose') {
    if (!isConductor) return phoneWait('SIGNAL IN PROGRESS', `${playerById(state.signal.conductorId)?.name || 'The conductor'} is composing.`, 'Study the big screen—your choices arrive next.');
    return `
      <section class="phone-card composer-card">
        ${phoneRoundBar()}
        <span class="eyebrow">YOUR SECRET</span>
        <h1 class="secret-prompt">${escapeHtml(state.signal.answer)}</h1>
        <div class="how-to-card"><b>HOW TO PLAY</b><span>1. Tap three clues that fit the secret.</span><span>2. Tap a clue again to remove it.</span><span>3. Preview the sequence, then send it.</span></div>
        <div class="signal-draft"><span>YOUR 3-CUE SIGNAL</span><div>${signalDraft.map((id, index) => signalTile(id, index + 1, false)).join('')}${Array.from({ length: 3 - signalDraft.length }, (_, index) => `<i>${signalDraft.length + index + 1}</i>`).join('')}</div></div>
        <div class="signal-palette">${state.signal.palette.map((id) => {
          const selected = signalDraft.indexOf(id);
          const tile = SIGNAL_TILE_MAP[id];
          return `<div class="signal-choice ${selected >= 0 ? 'is-selected' : ''}"><button data-action="set-signal-tile" data-tile="${id}" aria-pressed="${selected >= 0}"><b>${tile?.icon || '◉'}</b><span>${escapeHtml(tile?.label || id)}</span>${selected >= 0 ? `<i>${selected + 1}</i>` : ''}</button><button data-action="preview-signal-tile" data-tile="${id}" aria-label="Preview ${escapeAttr(tile?.label || id)}">▶</button></div>`;
        }).join('')}</div>
        <div class="composer-actions"><button class="button button--secondary" data-action="preview-signal" ${signalDraft.length ? '' : 'disabled'}>▶ Preview sequence</button><button class="button button--primary" data-action="submit-signal" ${signalDraft.length === 3 ? '' : 'disabled'}>Send 3 clues</button></div>
      </section>`;
  }
  if (state.phase === 'signal-guess') {
    if (isConductor) return phoneWait('SIGNAL SENT', 'They’re trying to read your mind.', `${state.progress.done}/${state.progress.total} guesses locked`);
    if (state.signal.guessed) return phoneWait('GUESS LOCKED', 'Watch the big screen for the reveal.', `${state.progress.done}/${state.progress.total} players ready`);
    return `<section class="phone-card choice-card">${phoneRoundBar()}<span class="eyebrow">WHAT DOES IT MEAN?</span><h1>Decode the signal</h1><div class="answer-grid">${state.signal.options.map((answer) => `<button data-action="guess-signal" data-answer="${escapeAttr(answer)}">${escapeHtml(answer)}</button>`).join('')}</div></section>`;
  }
  return phoneResults(me);
}

function renderStoryPlayer(me) {
  if (state.phase === 'story-write') {
    if (state.story.submitted) return phoneWait('BEAT LOCKED', 'The circuit is collecting everyone’s twist.', `${state.progress.done}/${state.progress.total} finished`);
    return `
      <section class="phone-card writer-card">
        ${phoneRoundBar()}
        <span class="eyebrow">THE STORY SO FAR</span>
        <h1>${escapeHtml(state.story.setup)}</h1>
        <div class="secret-direction"><span>YOUR SECRET DIRECTION</span><b>${escapeHtml(state.story.direction)}</b></div>
        <form id="story-form">
          <label for="story-text">Write the very next beat</label>
          <textarea id="story-text" name="text" maxlength="180" rows="5" placeholder="Then…" required></textarea>
          <div class="character-count"><span id="story-count">0</span>/180</div>
          <button class="button button--primary button--wide">Lock story beat</button>
        </form>
      </section>`;
  }
  if (state.phase === 'story-vote') {
    if (state.story.voted) return phoneWait('VOTE LOCKED', 'The room is choosing canon.', `${state.progress.done}/${state.progress.total} votes ready`);
    return `
      <section class="phone-card vote-card">
        ${phoneRoundBar()}
        <span class="eyebrow">READ THE CIRCUIT</span>
        <h1>Decode the missions. Pick one canon.</h1>
        <form id="story-vote-form">
          <div class="vote-stories">${state.story.submissions.map((entry, index) => entry.isMine ? `
            <article class="vote-story is-mine"><span>YOUR BEAT</span><p>${escapeHtml(entry.text)}</p></article>` : `
            <article class="vote-story">
              <div class="vote-story__top"><span>BEAT ${index + 1}</span><label><input type="radio" name="canon" value="${entry.id}" required> Make canon</label></div>
              <p>${escapeHtml(entry.text)}</p>
              <label class="mission-select">Their secret direction<select name="match-${entry.id}" required><option value="">Choose…</option>${state.story.directionOptions.map((direction) => `<option>${escapeHtml(direction)}</option>`).join('')}</select></label>
            </article>`).join('')}</div>
          <button class="button button--primary button--wide">Lock predictions</button>
        </form>
      </section>`;
  }
  return phoneResults(me);
}

function renderVectorPlayer(me) {
  if (state.phase === 'vector-command') {
    if (state.vector.submitted) return phoneWait('THRUSTER LOCKED', 'Your vector is in the flight computer.', `${state.progress.done}/${state.progress.total} crew commands ready`);
    const directions = [
      [225, '↖', 'NW'], [270, '↑', 'N'], [315, '↗', 'NE'],
      [180, '←', 'W'], [0, '→', 'E'],
      [135, '↙', 'SW'], [90, '↓', 'S'], [45, '↘', 'SE'],
    ];
    return `
      <section class="phone-card vector-controller">
        ${phoneRoundBar()}
        <span class="eyebrow">CALIBRATE YOUR THRUSTER</span>
        <h1>${escapeHtml(state.vector.course.objective)}</h1>
        <div class="quirk-card"><span>YOUR ENGINE</span><b>${escapeHtml(state.vector.quirk?.name || 'True North')}</b><p>${escapeHtml(state.vector.quirk?.detail || '')}</p></div>
        <div class="compass-pad" aria-label="Thruster direction">${directions.map(([angle, arrow, label]) => `<button class="${vectorDraft.angle === angle ? 'is-selected' : ''}" data-action="set-vector" data-angle="${angle}" aria-pressed="${vectorDraft.angle === angle}"><b>${arrow}</b><span>${label}</span></button>`).join('')}</div>
        <div class="control-group"><div class="control-label"><span>Raw power</span><b id="vector-power-value">${vectorDraft.power}%</b></div><input class="range" data-input="vector-power" type="range" min="10" max="100" value="${vectorDraft.power}"></div>
        <button class="button button--primary button--wide" data-action="submit-vector">Lock thruster command</button>
      </section>`;
  }
  return phoneResults(me);
}

function renderCrowdPlayer(me) {
  if (state.phase === 'crowd-place') {
    if (state.crowd.submitted) return phoneWait(state.crowd.isForecaster ? 'FORECAST LOCKED' : 'PULSE LOCKED', state.crowd.isForecaster ? 'The room is taking shape.' : 'Your take is flowing anonymously.', `${state.progress.done}/${state.progress.total} placements ready`);
    const prompt = state.crowd.prompt;
    return `
      <section class="phone-card crowd-controller">
        ${phoneRoundBar()}
        <span class="eyebrow">${state.crowd.isForecaster ? 'YOU ARE THE FORECASTER' : 'PLACE YOUR HONEST TAKE'}</span>
        <h1>${escapeHtml(prompt.question)}</h1>
        <p>${state.crowd.isForecaster ? 'Predict the average position of everyone else.' : 'There is no right answer. Your point stays anonymous until the final reveal.'}</p>
        <div class="current-preview">
          <span class="preview-label preview-label--left">${escapeHtml(prompt.xLow)}</span><span class="preview-label preview-label--right">${escapeHtml(prompt.xHigh)}</span>
          <span class="preview-label preview-label--top">${escapeHtml(prompt.yHigh)}</span><span class="preview-label preview-label--bottom">${escapeHtml(prompt.yLow)}</span>
          <i id="current-dot" style="--x:${crowdDraft.x}%;--y:${crowdDraft.y}%">${state.crowd.isForecaster ? '★' : '●'}</i>
        </div>
        <form id="crowd-placement-form">
          <label class="axis-control"><span><b>Horizontal</b><small>${escapeHtml(prompt.xLow)} ↔ ${escapeHtml(prompt.xHigh)}</small></span><input class="range" data-input="crowd-x" type="range" min="0" max="100" value="${crowdDraft.x}"></label>
          <label class="axis-control"><span><b>Vertical</b><small>${escapeHtml(prompt.yHigh)} ↔ ${escapeHtml(prompt.yLow)}</small></span><input class="range" data-input="crowd-y" type="range" min="0" max="100" value="${crowdDraft.y}"></label>
          ${state.crowd.isForecaster ? `<label class="axis-control"><span><b>Confidence</b><small id="confidence-value">${crowdDraft.confidence}%</small></span><input class="range" data-input="crowd-confidence" type="range" min="0" max="100" value="${crowdDraft.confidence}"></label>` : `<label class="broadcast-input">Your anonymous broadcast<input name="label" maxlength="28" value="${escapeAttr(crowdDraft.label)}" placeholder="A tiny caption for your take" required><small>2–28 characters</small></label>`}
          <button class="button button--primary button--wide">${state.crowd.isForecaster ? 'Lock forecast' : 'Send pulse'}</button>
        </form>
      </section>`;
  }
  if (state.phase === 'crowd-vote') {
    if (!state.crowd.canVote) return phoneWait('CURRENT REVEALED', 'Your pulse is the only broadcast this round.', 'Watch the big screen for the result.');
    if (state.crowd.voted) return phoneWait('SPARK LOCKED', 'Your favorite broadcast has your vote.', `${state.progress.done}/${state.progress.total} spark votes ready`);
    return `
      <section class="phone-card crowd-voter">
        ${phoneRoundBar()}
        <span class="eyebrow">SPARK A BROADCAST</span>
        <h1>Which anonymous take made the current better?</h1>
        <form id="crowd-vote-form"><div class="broadcast-votes">${state.crowd.pulses.map((pulse) => pulse.isMine ? `<div class="broadcast-vote is-mine"><span>YOUR PULSE</span><b>“${escapeHtml(pulse.label)}”</b></div>` : `<label class="broadcast-vote"><input type="radio" name="pulse" value="${escapeAttr(pulse.id)}" required><span>SPARK THIS</span><b>“${escapeHtml(pulse.label)}”</b></label>`).join('')}</div><button class="button button--primary button--wide">Lock spark vote</button></form>
      </section>`;
  }
  return phoneResults(me);
}

function renderCollisionPlayer(me) {
  const isSolver = state.collision.isSolver;
  if (state.phase === 'collision-write') {
    if (isSolver) return phoneWait('YOU ARE THE SOLVER', 'Everyone is writing one-word clues for you.', `Category: ${state.collision.category}`);
    if (state.collision.submitted) return phoneWait('CLUE LOCKED', 'If another player wrote the same word, both clues disappear.', `${state.progress.done}/${state.progress.total} clues ready`);
    return `<section class="phone-card collision-controller">
      ${phoneRoundBar()}
      <span class="eyebrow">SECRET WORD · ${escapeHtml(state.collision.category)}</span>
      <h1>${escapeHtml(state.collision.target)}</h1>
      <div class="how-to-card"><b>THE ONLY RULE</b><span>Write one helpful word. If anyone matches you, both clues collide and vanish.</span></div>
      <form id="collision-clue-form"><label>One-word clue<input name="clue" maxlength="24" value="${escapeAttr(textDrafts.collisionClue)}" autocomplete="off" placeholder="Helpful but uncommon" required></label><button class="button button--primary button--wide">Lock clue</button></form>
    </section>`;
  }
  if (state.phase === 'collision-guess') {
    if (!isSolver) return phoneWait('CLUES REVEALED', `${state.collision.clues.length} clue${state.collision.clues.length === 1 ? '' : 's'} survived.`, 'The solver gets one guess.');
    if (state.collision.guessed) return phoneWait('GUESS LOCKED', 'Watch the big screen for the reveal.', 'The answer is coming up.');
    return `<section class="phone-card collision-controller">
      ${phoneRoundBar()}
      <span class="eyebrow">CATEGORY · ${escapeHtml(state.collision.category)}</span>
      <h1>What is the secret word?</h1>
      <div class="clue-cloud clue-cloud--phone">${state.collision.clues.length ? state.collision.clues.map((clue) => `<span>${escapeHtml(clue.text)}</span>`).join('') : '<em>Every clue collided.</em>'}</div>
      <form id="collision-guess-form"><input name="guess" maxlength="24" value="${escapeAttr(textDrafts.collisionGuess)}" autocomplete="off" placeholder="Your one guess" required><button class="button button--primary button--wide">Lock guess</button></form>
    </section>`;
  }
  return phoneResults(me);
}

function renderVibePlayer(me) {
  if (state.phase !== 'vibe-pick') return phoneResults(me);
  const subject = playerById(state.vibe.subjectId);
  if (state.vibe.submitted) return phoneWait(state.vibe.isSubject ? 'VIBE LOCKED' : 'READ LOCKED', state.vibe.isSubject ? 'Everyone is trying to predict you.' : `You made your call on ${subject?.name || 'the hot seat'}.`, `${state.progress.done}/${state.progress.total} ready`);
  return `<section class="phone-card vibe-controller">
    ${phoneRoundBar()}
    <span class="eyebrow">${state.vibe.isSubject ? 'YOU ARE IN THE HOT SEAT' : `READ ${escapeHtml(subject?.name || 'THE HOT SEAT')}`}</span>
    <h1>${escapeHtml(state.vibe.prompt.question)}</h1>
    <p>${state.vibe.isSubject ? 'Choose honestly. Your answers stay secret until everyone locks.' : 'Predict both ends of their answer.'}</p>
    <form id="vibe-form">
      <label class="vibe-select"><span>💚 ${state.vibe.isSubject ? 'YOUR FAVORITE' : 'THEIR FAVORITE'}</span><select name="favorite" required><option value="">Choose…</option>${state.vibe.prompt.options.map((option) => `<option ${vibeDraft.favorite === option ? 'selected' : ''}>${escapeHtml(option)}</option>`).join('')}</select></label>
      <label class="vibe-select"><span>⛔ ${state.vibe.isSubject ? 'YOUR HARD NO' : 'THEIR HARD NO'}</span><select name="nope" required><option value="">Choose…</option>${state.vibe.prompt.options.map((option) => `<option ${vibeDraft.nope === option ? 'selected' : ''}>${escapeHtml(option)}</option>`).join('')}</select></label>
      <button class="button button--primary button--wide">Lock both choices</button>
    </form>
  </section>`;
}

function renderBluffPlayer(me) {
  if (state.phase === 'bluff-write') {
    if (state.bluff.submitted) return phoneWait('FILED', 'Your answer is now mixed in with the truth.', `${state.progress.done}/${state.progress.total} files ready`);
    return `<section class="phone-card bluff-controller">
      ${phoneRoundBar()}
      <span class="eyebrow">FILE A BELIEVABLE LIE</span>
      <h1>${escapeHtml(state.bluff.question)}</h1>
      <p>The real answer will be added automatically. Make yours plausible enough to steal votes.</p>
      <form id="bluff-form"><label>Fake answer<input name="bluff" maxlength="80" value="${escapeAttr(textDrafts.bluff)}" autocomplete="off" placeholder="Something suspiciously convincing" required></label><button class="button button--primary button--wide">File bluff</button></form>
    </section>`;
  }
  if (state.phase === 'bluff-vote') {
    if (state.bluff.voted) return phoneWait('CHOICE LOCKED', 'The Bureau is counting who fooled whom.', `${state.progress.done}/${state.progress.total} votes ready`);
    return `<section class="phone-card bluff-controller">
      ${phoneRoundBar()}
      <span class="eyebrow">FIND THE TRUE FILE</span>
      <h1>${escapeHtml(state.bluff.question)}</h1>
      <form id="bluff-vote-form"><div class="bluff-votes">${state.bluff.options.map((option) => option.isMine ? `<div class="bluff-vote is-mine"><span>YOUR BLUFF</span><b>${escapeHtml(option.text)}</b></div>` : `<label class="bluff-vote"><input type="radio" name="option" value="${escapeAttr(option.id)}" required><span>CHOOSE THIS</span><b>${escapeHtml(option.text)}</b></label>`).join('')}</div><button class="button button--primary button--wide">Lock answer</button></form>
    </section>`;
  }
  return phoneResults(me);
}

function renderSketchPlayer(me) {
  if (state.phase === 'sketch-draw') {
    if (state.sketch.submitted) return phoneWait('SKETCH SENT', 'Your drawing is about to switch phones.', `${state.progress.done}/${state.progress.total} artists ready`);
    return `<section class="phone-card sketch-controller">
      ${phoneRoundBar()}
      <span class="eyebrow">DRAW THIS SECRET</span>
      <h1>${escapeHtml(state.sketch.prompt)}</h1>
      <p>Drag across the grid. Simple, bold shapes read best.</p>
      ${sketchEditor()}
      <button class="button button--primary button--wide" data-action="submit-sketch">Send sketch</button>
    </section>`;
  }
  if (state.phase === 'sketch-caption') {
    if (state.sketch.captioned) return phoneWait('CAPTION LOCKED', 'The original prompt stays hidden until the gallery reveal.', `${state.progress.done}/${state.progress.total} captions ready`);
    return `<section class="phone-card sketch-controller">
      ${phoneRoundBar()}
      <span class="eyebrow">WHAT DO YOU THINK THIS IS?</span>
      <h1>Name the mystery sketch.</h1>
      ${sketchGrid(state.sketch.assigned?.cells || '0'.repeat(192))}
      <form id="sketch-caption-form"><label>Your caption<input name="caption" maxlength="72" value="${escapeAttr(textDrafts.sketchCaption)}" autocomplete="off" placeholder="A confident interpretation" required></label><button class="button button--primary button--wide">Lock caption</button></form>
    </section>`;
  }
  if (state.phase === 'sketch-vote') {
    if (!state.sketch.canVote) return phoneWait('GALLERY OPEN', 'Every pair includes your art or caption this round.', 'Watch the big screen for the reveal.');
    if (state.sketch.voted) return phoneWait('MASTERPIECE PICKED', 'Your gallery vote is locked.', `${state.progress.done}/${state.progress.total} votes ready`);
    return `<section class="phone-card sketch-controller">
      ${phoneRoundBar()}
      <span class="eyebrow">PICK THE BEST ACCIDENT</span>
      <h1>${state.sketch.openVoting ? 'Small room: every combo is eligible.' : 'Vote for another team’s combo.'}</h1>
      <form id="sketch-vote-form"><div class="sketch-votes">${state.sketch.pairs.map((pair) => pair.isMine ? `<article class="sketch-vote is-mine">${sketchGrid(pair.cells)}<p>“${escapeHtml(pair.caption)}”</p><span>YOUR TEAM</span></article>` : `<label class="sketch-vote"><input type="radio" name="pair" value="${escapeAttr(pair.id)}" required>${sketchGrid(pair.cells)}<p>“${escapeHtml(pair.caption)}”</p><span>VOTE FOR THIS</span></label>`).join('')}</div><button class="button button--primary button--wide">Lock gallery vote</button></form>
    </section>`;
  }
  return phoneResults(me);
}

function phoneResults(me) {
  const score = me.score.toLocaleString();
  if (state.results?.kind === 'signal') {
    const guess = state.results.guesses.find((entry) => entry.playerId === me.id);
    const conductor = state.results.conductorId === me.id;
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${conductor ? '📡' : guess?.correct ? '🎯' : '🫠'}</div><span class="eyebrow">ROUND ${state.round}</span><h1>${conductor ? `+${state.results.conductorPoints}` : guess?.correct ? `+${guess.points}` : 'Not this time'}</h1><p>The answer was <b>${escapeHtml(state.results.answer)}</b>.</p><div class="score-chip">${score} total points</div></section>`;
  }
  if (state.results?.kind === 'vector') {
    const points = state.results.playerPoints.find((entry) => entry.playerId === me.id)?.points || 0;
    const ace = state.results.aceIds.includes(me.id);
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${state.results.landed ? '🔓' : state.results.collision ? '💥' : ace ? '🎯' : '🛰️'}</div><span class="eyebrow">${ace ? 'THRUSTER ACE' : 'CREW MANEUVER'}</span><h1>+${points}</h1><p>${state.results.landed ? 'Your crew cracked the Vector Vault.' : ace ? 'Your corrected thrust was closest to the ideal vector.' : `The core missed center by ${state.results.distance} units.`}</p><div class="score-chip">${score} total points</div></section>`;
  }
  if (state.results?.kind === 'crowd') {
    const forecaster = state.results.forecasterId === me.id;
    const pulse = state.results.pulses.find((entry) => entry.authorId === me.id);
    const points = forecaster ? state.results.forecastPoints : pulse?.points || 0;
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${forecaster ? '★' : pulse?.sparkVotes ? '⚡' : pulse?.liveWire ? '〽' : '≈'}</div><span class="eyebrow">${forecaster ? 'FORECAST RESULT' : pulse?.liveWire ? 'LIVE WIRE' : 'YOUR PULSE'}</span><h1>+${points}</h1><p>${forecaster ? `Your prediction missed the room by ${state.results.error} units.` : `${pulse?.sparkVotes || 0} spark vote${pulse?.sparkVotes === 1 ? '' : 's'} for “${escapeHtml(pulse?.label || '')}”`}</p><div class="score-chip">${score} total points</div></section>`;
  }
  if (state.results?.kind === 'collision') {
    const solver = state.results.solverId === me.id;
    const clue = state.results.clues.find((entry) => entry.authorId === me.id);
    const points = solver ? state.results.solverPoints : clue?.points || 0;
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${solver ? state.results.correct ? '🎯' : '🧩' : clue?.survived ? '✹' : '💥'}</div><span class="eyebrow">${solver ? 'SOLVER RESULT' : clue?.survived ? 'CLUE SURVIVED' : 'CLUE COLLIDED'}</span><h1>+${points}</h1><p>The word was <b>${escapeHtml(state.results.target.word)}</b>${clue ? ` · your clue: “${escapeHtml(clue.text)}”` : ''}.</p><div class="score-chip">${score} total points</div></section>`;
  }
  if (state.results?.kind === 'vibe') {
    const subject = state.results.subjectId === me.id;
    const prediction = state.results.predictions.find((entry) => entry.playerId === me.id);
    const points = subject ? state.results.subjectPoints : prediction?.points || 0;
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${subject ? '🪞' : prediction?.favoriteCorrect && prediction?.nopeCorrect ? '🔮' : '♡'}</div><span class="eyebrow">${subject ? 'YOUR VIBE' : 'YOUR READ'}</span><h1>+${points}</h1><p>Favorite: <b>${escapeHtml(state.results.choice.favorite)}</b> · hard no: <b>${escapeHtml(state.results.choice.nope)}</b>.</p><div class="score-chip">${score} total points</div></section>`;
  }
  if (state.results?.kind === 'bluff') {
    const vote = state.results.votes.find((entry) => entry.playerId === me.id);
    const authored = state.results.options.find((option) => option.authorIds.includes(me.id));
    const truthBonus = state.results.truthTellers.includes(me.id) ? 750 : 0;
    const points = (vote?.points || 0) + (authored?.authorPoints || 0) + truthBonus;
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${truthBonus ? '🧠' : vote?.correct ? '🔎' : authored?.fooled ? '🕶️' : '📁'}</div><span class="eyebrow">BUREAU RESULT</span><h1>+${points}</h1><p>The truth was <b>${escapeHtml(state.results.question.answer)}</b>${authored ? ` · your bluff fooled ${authored.fooled}` : ''}.</p><div class="score-chip">${score} total points</div></section>`;
  }
  if (state.results?.kind === 'sketch') {
    const pairs = state.results.pairs.filter((entry) => entry.artistId === me.id || entry.captionerId === me.id);
    const points = pairs.reduce((sum, pair) => sum + pair.points, 0);
    const votes = pairs.reduce((sum, pair) => sum + pair.votes, 0);
    return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${votes ? '🖼️' : '✏️'}</div><span class="eyebrow">YOUR TWO COLLABS</span><h1>+${points}</h1><p>Your drawing and your caption teams earned ${votes} gallery vote${votes === 1 ? '' : 's'} together.</p><div class="score-chip">${score} total points</div></section>`;
  }
  const mine = state.results?.submissions.find((entry) => entry.authorId === me.id);
  return `<section class="phone-card glass-card ready-card"><div class="avatar-hero">${mine?.canonVotes ? '⭐' : '✍️'}</div><span class="eyebrow">ROUND ${state.round}</span><h1>+${mine?.points || 0} for your beat</h1><p>${mine?.canonVotes || 0} canon vote${mine?.canonVotes === 1 ? '' : 's'} · decoded ${mine?.decodedBy || 0}×</p><div class="score-chip">${score} total points</div></section>`;
}

function waitingPanel(title, detail) {
  return `<div class="waiting-panel"><div class="waiting-orbit"><span></span><span></span><span></span></div><span class="eyebrow">ROUND ${state.round} OF ${state.maxRounds}</span><h1>${title}</h1><p>${detail}</p>${progressMeter()}<button class="text-button" data-action="finish-phase">End phase early</button></div>`;
}

function phoneWait(kicker, title, detail) {
  return `<section class="phone-card glass-card ready-card">${phoneRoundBar()}<div class="phone-pulse"><i></i></div><span class="eyebrow">${escapeHtml(kicker)}</span><h1>${escapeHtml(title)}</h1><p>${escapeHtml(detail)}</p></section>`;
}

function gameCard(id, game, selected) {
  return `<button class="party-game-card ${selected ? 'is-selected' : ''}" data-action="pick-landing-game" data-game="${id}" aria-pressed="${selected}" style="--game-accent:${game.accent}">
    <div class="party-game-card__art"><span>${game.icon}</span><i></i><i></i><i></i></div>
    <span class="eyebrow">${game.kicker}</span><h3>${game.name}</h3><p>${game.description}</p><small>${game.detail}</small>
    <b class="selected-check">✓ Selected</b>
  </button>`;
}

function miniGame(id, game, selected) {
  return `<button class="mini-game ${selected ? 'is-selected' : ''}" data-action="select-game" data-game="${id}" style="--game-accent:${game.accent}"><span>${game.icon}</span><div><b>${game.name}</b><small>${game.kicker}</small></div><i>${selected ? '✓' : ''}</i></button>`;
}

function playerLobby() {
  return `<aside class="player-lobby glass-card"><div class="section-heading"><div><span class="eyebrow">PARTY ROSTER</span><h2>${state.players.length} joined</h2></div></div><div class="player-grid">${state.players.map((player) => `<div class="player-chip ${player.connected ? '' : 'is-offline'}"><span>${player.avatar}</span><b>${escapeHtml(player.name)}</b><i>${player.connected ? 'ready' : 'reconnecting'}</i><button data-action="kick-player" data-id="${player.id}" aria-label="Remove ${escapeAttr(player.name)}">×</button></div>`).join('')}${Array.from({ length: Math.max(0, 2 - state.players.length) }, () => '<div class="player-chip player-chip--empty"><span>+</span><b>Waiting…</b></div>').join('')}</div></aside>`;
}

function scoreboard(embedded = false) {
  const rows = [...state.players].sort((a, b) => b.score - a.score);
  return `<aside class="scoreboard glass-card ${embedded ? 'scoreboard--embedded' : ''}"><span class="eyebrow">SCOREBOARD</span>${rows.map((player, index) => `<div class="score-row"><i>${index + 1}</i><span>${player.avatar}</span><b>${escapeHtml(player.name)}</b><strong>${player.score.toLocaleString()}</strong></div>`).join('')}</aside>`;
}

function smallScoreboard() {
  return `<div class="small-scoreboard">${[...state.players].sort((a, b) => b.score - a.score).map((player) => `<div><span>${player.avatar} ${escapeHtml(player.name)}</span><b>${player.score.toLocaleString()}</b></div>`).join('')}</div>`;
}

function smallPlayers() {
  return `<div class="small-players">${state.players.map((player) => `<span title="${escapeAttr(player.name)}">${player.avatar}</span>`).join('')}</div>`;
}

function progressMeter() {
  const percent = state.progress.total ? Math.round((state.progress.done / state.progress.total) * 100) : 0;
  return `<div class="progress"><div><span>${state.progress.done}/${state.progress.total} ready</span><b class="timer__number"></b></div><i><b style="width:${percent}%"></b></i></div>`;
}

function roundBar() {
  return `<div class="round-bar"><span>${GAMES[state.selectedGame].icon} ${GAMES[state.selectedGame].name}</span><b>ROUND ${state.round} / ${state.maxRounds}</b><div class="timer"><i class="timer__fill"></i></div></div>`;
}

function phoneRoundBar() {
  return `<div class="phone-round"><span>ROUND ${state.round}/${state.maxRounds}</span><div class="timer"><i class="timer__fill"></i></div><b class="timer__number"></b></div>`;
}

function nextButton() {
  return `<button class="button button--primary button--xl" data-action="next-round">${state.round >= state.maxRounds ? 'See final scores' : 'Next round'}</button>`;
}

function signalTile(id, order = null) {
  const tile = SIGNAL_TILE_MAP[id] || { icon: '◉', label: id };
  return `<div class="signal-tile">${order ? `<i>${order}</i>` : ''}<b>${tile.icon}</b><span>${escapeHtml(tile.label)}</span></div>`;
}

function sketchGrid(cells) {
  const safe = /^[0-3]{192}$/.test(cells || '') ? cells : '0'.repeat(192);
  return `<div class="sketch-grid" aria-label="Pixel sketch">${[...safe].map((color) => `<i class="ink-${color}"></i>`).join('')}</div>`;
}

function sketchEditor() {
  return `<div class="sketch-tools"><div>${[['1', 'Purple'], ['2', 'Pink'], ['3', 'Lime'], ['0', 'Eraser']].map(([color, label]) => `<button class="ink-${color} ${sketchColor === color ? 'is-selected' : ''}" data-action="set-sketch-color" data-color="${color}" aria-label="${label}">${color === '0' ? '⌫' : ''}</button>`).join('')}</div><button data-action="clear-sketch">Clear</button></div><div id="sketch-editor" class="sketch-grid sketch-grid--editor" role="group" aria-label="Drawing grid" aria-describedby="sketch-instructions">${[...sketchDraft].map((color, index) => `<button class="ink-${color}" data-cell="${index}" tabindex="${index === 0 ? 0 : -1}" aria-label="Drawing cell ${index + 1}"></button>`).join('')}</div><p id="sketch-instructions" class="drawing-help">Drag to draw. Keyboard: use arrow keys to move, then Space to paint.</p>`;
}

function reactionBar() {
  return `<nav class="reaction-bar" aria-label="Quick reactions">${['👏', '😂', '😮', '🔥', '💡'].map((emoji) => `<button data-action="reaction" data-emoji="${emoji}">${emoji}</button>`).join('')}</nav>`;
}

function brandHeader(showHome = true) {
  return `<header class="topbar"><a class="brand" href="https://joyboxlabs.com" aria-label="Joybox Labs home"><span>J</span><b>Joybox<sup>™</sup></b><em>PARTY</em></a><div>${showHome ? '<a class="top-link" href="https://joyboxlabs.com">All games</a>' : ''}<button class="sound-button" data-action="toggle-sound" aria-label="Toggle sound">${audio.enabled ? '🔊' : '🔇'}</button></div></header>`;
}

function gameHeader() {
  return `<header class="game-topbar"><a class="brand brand--small" href="/"><span>J</span><b>Joybox</b><em>PARTY</em></a><div class="game-topbar__room"><span>ROOM</span><button data-action="copy-link">${escapeHtml(state.code)}</button></div><div class="connection ${socket?.readyState === WebSocket.OPEN ? 'is-online' : ''}"><i></i><span>${socket?.readyState === WebSocket.OPEN ? 'Live' : 'Reconnecting'}</span></div><button class="sound-button" data-action="toggle-sound">${audio.enabled ? '🔊' : '🔇'}</button></header>`;
}

function footer() {
  return `<footer class="landing-footer"><span>© ${new Date().getFullYear()} Joybox Labs</span><a href="https://joyboxlabs.com/privacy.html">Privacy</a><a href="https://joyboxlabs.com/terms.html">Terms</a></footer>`;
}

function afterRender() {
  if (session.role === 'host' && state.phase === 'lobby') {
    const canvas = document.getElementById('room-qr');
    if (canvas) QRCode.toCanvas(canvas, joinUrl(), { width: 164, margin: 1, color: { dark: '#161129', light: '#ffffff' } }).catch(() => {});
  }
  const storyInput = document.getElementById('story-text');
  const storyCount = document.getElementById('story-count');
  if (storyInput && storyCount) storyCount.textContent = storyInput.value.length;
  syncTimer();
}

function connect() {
  clearTimeout(reconnectTimer);
  renderConnecting();
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const query = new URLSearchParams({ role: session.role, id: session.id || '' });
  socket = new WebSocket(
    `${protocol}//${location.host}/api/rooms/${session.code}/ws?${query}`,
    ['joybox-party-v1', session.token],
  );
  socket.addEventListener('open', () => {
    reconnectAttempt = 0;
    audio.unlock().catch(() => {});
  });
  socket.addEventListener('message', onSocketMessage);
  socket.addEventListener('close', (event) => {
    if (event.code === 4003) {
      clearSession(session.code);
      showToast('The host removed you from the room.');
      setTimeout(() => renderJoin(session.code), 900);
      return;
    }
    if (event.code === 4001 || event.code === 4004) {
      clearTimeout(reconnectTimer);
      clearInterval(timerInterval);
      if (event.code === 4004) clearSession(session.code);
      app.innerHTML = `${brandHeader(false)}<main class="shell shell--center"><section class="connection-card">
        <h1>${event.code === 4001 ? 'Playing in another tab' : 'This party has ended'}</h1>
        <p>${event.code === 4001 ? 'Continue in your other tab, or reload this one to reconnect here.' : 'Rooms last six hours. Start a new party to keep playing.'}</p>
        <a class="button button--primary" href="/">Back to Joybox Party</a></section></main>`;
      return;
    }
    scheduleReconnect();
  });
  socket.addEventListener('error', () => socket.close());
}

function onSocketMessage(event) {
  let message;
  try { message = JSON.parse(event.data); } catch { return; }
  if (message.type === 'error') {
    showToast(message.message, true);
    if (state) renderGame();
    return;
  }
  if (message.type === 'reaction') {
    showReaction(message);
    return;
  }
  if (message.type !== 'state') return;
  const previous = state;
  state = message.state;
  const stateKey = `${state.selectedGame}:${state.phase}:${state.round}`;
  if (stateKey !== lastStateKey) {
    signalDraft = [];
    vectorDraft = { angle: 315, power: 70 };
    crowdDraft = { x: 50, y: 50, confidence: 60, label: '' };
    vibeDraft = { favorite: '', nope: '' };
    sketchDraft = '0'.repeat(192);
    sketchColor = '1';
    textDrafts = { collisionClue: '', collisionGuess: '', bluff: '', sketchCaption: '' };
    if (state.phase === 'signal-guess' && state.signal?.clue) audio.playSignal(state.signal.clue).catch(() => {});
    if (state.phase === 'crowd-vote') audio.playEffect('current');
    if (state.phase === 'round-results') {
      visuals.celebrate();
      if (state.results?.kind === 'vector') {
        audio.playEffect('launch');
        setTimeout(() => audio.playEffect(state.results?.landed ? 'vault' : 'drift'), 650);
      } else audio.playCue('reveal');
    }
    if (state.phase === 'game-over') {
      visuals.celebrate();
      audio.playCue('win');
    }
    lastStateKey = stateKey;
  } else if (previous?.progress?.done !== state.progress?.done) {
    audio.playCue('tap');
  }
  renderGame();
}

function scheduleReconnect() {
  reconnectAttempt += 1;
  if (reconnectAttempt > 6) {
    app.innerHTML = `${brandHeader(false)}<main class="shell shell--center"><section class="connection-card">
      <h1>We couldn't reconnect</h1><p>Check your connection. The room may have ended.</p>
      <button class="button button--primary" data-action="retry-connection">Try again</button>
      <a class="button button--ghost" href="/">Back to games</a></section></main>`;
    return;
  }
  const delay = Math.min(10_000, 600 * 2 ** reconnectAttempt);
  if (state) renderGame();
  reconnectTimer = setTimeout(connect, delay);
}

function send(message) {
  if (socket?.readyState !== WebSocket.OPEN) {
    showToast('Reconnecting—try again in a moment.', true);
    return false;
  }
  try { socket.send(JSON.stringify(message)); return true; }
  catch { showToast('Connection interrupted. Please try again.', true); return false; }
}

app.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-action]');
  if (!button) return;
  const action = button.dataset.action;

  if (action === 'share-result' && state?.phase === 'game-over') {
    const me = playerById(session.id);
    const game = GAMES[state.selectedGame];
    const score = me ? `${state.winnerIds.includes(me.id) ? '🏆 Winner' : '✦ Finished'} · ${me.score.toLocaleString()} points` : `🏆 Party complete · ${state.players.length} players`;
    await shareResult(`JoyBoxLabs · ${game.name}\n${score}\n${state.round} rounds of friendly competition\nStart your own party →\nhttps://party.joyboxlabs.com/`);
  } else if (action === 'retry-connection') {
    reconnectAttempt = 0;
    connect();
  } else if (action === 'pick-landing-game') {
    selectedLandingGame = button.dataset.game;
    renderLanding();
  } else if (action === 'create-room') {
    await createRoom(button);
  } else if (action === 'focus-join') {
    document.getElementById('join-panel')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => document.getElementById('room-code')?.focus(), 350);
  } else if (action === 'toggle-sound') {
    audio.toggle();
    button.textContent = audio.enabled ? '🔊' : '🔇';
  } else if (action === 'select-game') {
    send({ type: 'select-game', game: button.dataset.game });
  } else if (action === 'start-game') {
    send({ type: 'start-game' });
  } else if (action === 'next-round') {
    send({ type: 'next-round' });
  } else if (action === 'return-lobby') {
    send({ type: 'return-lobby' });
  } else if (action === 'finish-phase') {
    send({ type: 'finish-phase' });
  } else if (action === 'kick-player') {
    send({ type: 'kick-player', playerId: button.dataset.id });
  } else if (action === 'set-signal-tile') {
    const tile = button.dataset.tile;
    const selected = signalDraft.indexOf(tile);
    if (selected >= 0) signalDraft.splice(selected, 1);
    else if (signalDraft.length < 3) signalDraft.push(tile);
    else return showToast('Your signal already has three clues.', true);
    renderGame();
  } else if (action === 'preview-signal-tile') {
    audio.playSignal({ tiles: [button.dataset.tile] }, true).catch(() => {});
  } else if (action === 'preview-signal' || action === 'play-signal') {
    audio.playSignal(action === 'preview-signal' ? { tiles: signalDraft } : state.signal?.clue, action === 'preview-signal').catch(() => {});
  } else if (action === 'submit-signal') {
    if (signalDraft.length !== 3) return showToast('Choose exactly three clues.', true);
    if (!send({ type: 'submit-signal', clue: { tiles: signalDraft } })) return;
    button.disabled = true;
  } else if (action === 'guess-signal') {
    if (!send({ type: 'guess-signal', answer: button.dataset.answer })) return;
    document.querySelectorAll('[data-action="guess-signal"]').forEach((item) => { item.disabled = true; });
  } else if (action === 'set-vector') {
    vectorDraft.angle = Number(button.dataset.angle);
    renderGame();
  } else if (action === 'submit-vector') {
    if (!send({ type: 'submit-vector', command: vectorDraft })) return;
    button.disabled = true;
  } else if (action === 'set-sketch-color') {
    sketchColor = button.dataset.color;
    renderGame();
  } else if (action === 'clear-sketch') {
    sketchDraft = '0'.repeat(192);
    renderGame();
  } else if (action === 'submit-sketch') {
    if (!/[1-3]/.test(sketchDraft)) return showToast('Draw at least one mark first.', true);
    if (!send({ type: 'submit-sketch', cells: sketchDraft })) return;
    button.disabled = true;
  } else if (action === 'reaction') {
    send({ type: 'reaction', emoji: button.dataset.emoji });
    showReaction({ emoji: button.dataset.emoji, playerId: session.id });
  } else if (action === 'copy-link') {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(joinUrl());
      showToast('Join link copied!');
    } catch { showToast('Copy is unavailable. Share the room code shown on screen.', true); }
  } else if (action === 'narrate-stories') {
    const words = [state.story.setup, ...state.story.submissions.map((entry, index) => `Beat ${index + 1}. ${entry.text}`)].join(' ');
    audio.speak(words);
  } else if (action === 'narrate-results') {
    const winner = [...state.results.submissions].sort((a, b) => b.points - a.points)[0];
    audio.speak(`The room chose: ${winner.text}`);
  } else if (action === 'forget-session') {
    clearSession(session.code);
    location.href = `/?room=${session.code}`;
  }
});


app.addEventListener('input', (event) => {
  if (event.target.dataset.input === 'vector-power') {
    vectorDraft.power = Number(event.target.value);
    const value = document.getElementById('vector-power-value');
    if (value) value.textContent = `${vectorDraft.power}%`;
  }
  if (event.target.dataset.input === 'crowd-x' || event.target.dataset.input === 'crowd-y') {
    const axis = event.target.dataset.input === 'crowd-x' ? 'x' : 'y';
    crowdDraft[axis] = Number(event.target.value);
    const dot = document.getElementById('current-dot');
    if (dot) dot.style.setProperty(`--${axis}`, `${crowdDraft[axis]}%`);
  }
  if (event.target.dataset.input === 'crowd-confidence') {
    crowdDraft.confidence = Number(event.target.value);
    const value = document.getElementById('confidence-value');
    if (value) value.textContent = `${crowdDraft.confidence}%`;
  }
  if (event.target.name === 'label') crowdDraft.label = event.target.value;
  if (event.target.name === 'favorite') vibeDraft.favorite = event.target.value;
  if (event.target.name === 'nope') vibeDraft.nope = event.target.value;
  if (event.target.name === 'clue') textDrafts.collisionClue = event.target.value;
  if (event.target.name === 'guess') textDrafts.collisionGuess = event.target.value;
  if (event.target.name === 'bluff') textDrafts.bluff = event.target.value;
  if (event.target.name === 'caption') textDrafts.sketchCaption = event.target.value;
  if (event.target.id === 'story-text') {
    const count = document.getElementById('story-count');
    if (count) count.textContent = event.target.value.length;
  }
  if (event.target.name === 'room') event.target.value = normalizeRoom(event.target.value);
});

app.addEventListener('submit', async (event) => {
  event.preventDefault();
  const form = event.target;
  if (form.id === 'landing-join') {
    const room = normalizeRoom(new FormData(form).get('room'));
    if (room.length === 6) location.href = `/?room=${room}`;
  } else if (form.id === 'player-join') {
    await joinRoom(form);
  } else if (form.id === 'story-form') {
    const text = new FormData(form).get('text');
    if (String(text).trim().length < 3) return showToast('Write a little more first.', true);
    if (!send({ type: 'submit-story', text })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'story-vote-form') {
    const data = new FormData(form);
    const canonId = data.get('canon');
    if (!canonId) return showToast('Choose one story for canon.', true);
    const matches = {};
    for (const entry of state.story.submissions) {
      if (!entry.isMine) matches[entry.id] = data.get(`match-${entry.id}`);
    }
    if (Object.values(matches).some((value) => !value)) return showToast('Decode every secret direction.', true);
    if (!send({ type: 'vote-story', vote: { canonId, matches } })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'crowd-placement-form') {
    const placement = { ...crowdDraft };
    if (!state.crowd.isForecaster) {
      placement.label = crowdDraft.label.trim();
      if (placement.label.length < 2) return showToast('Add a short broadcast first.', true);
    }
    if (!send({ type: 'submit-crowd', placement })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'crowd-vote-form') {
    const pulseId = new FormData(form).get('pulse');
    if (!pulseId) return showToast('Choose one broadcast to spark.', true);
    if (!send({ type: 'vote-crowd', pulseId })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'collision-clue-form') {
    const clue = textDrafts.collisionClue.trim();
    if (!/^[\p{L}\p{N}'-]+$/u.test(clue)) return showToast('Use one word only.', true);
    if (!send({ type: 'submit-collision-clue', clue })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'collision-guess-form') {
    const guess = textDrafts.collisionGuess.trim();
    if (!guess) return showToast('Enter your guess.', true);
    if (!send({ type: 'submit-collision-guess', guess })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'vibe-form') {
    const pick = { ...vibeDraft };
    if (!pick.favorite || !pick.nope || pick.favorite === pick.nope) return showToast('Choose two different answers.', true);
    if (!send({ type: 'submit-vibe', pick })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'bluff-form') {
    const answer = textDrafts.bluff.trim();
    if (answer.length < 2) return showToast('Write a believable answer.', true);
    if (!send({ type: 'submit-bluff', answer })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'bluff-vote-form') {
    const optionId = new FormData(form).get('option');
    if (!optionId) return showToast('Choose the answer you think is true.', true);
    if (!send({ type: 'vote-bluff', optionId })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'sketch-caption-form') {
    const caption = textDrafts.sketchCaption.trim();
    if (caption.length < 2) return showToast('Write a caption first.', true);
    if (!send({ type: 'submit-sketch-caption', caption })) return;
    form.querySelector('button').disabled = true;
  } else if (form.id === 'sketch-vote-form') {
    const pairId = new FormData(form).get('pair');
    if (!pairId) return showToast('Choose one gallery entry.', true);
    if (!send({ type: 'vote-sketch', pairId })) return;
    form.querySelector('button').disabled = true;
  }
});

async function createRoom(button) {
  button.disabled = true;
  button.textContent = 'Opening room…';
  try {
    await audio.unlock().catch(() => {});
    const response = await fetch('/api/rooms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game: selectedLandingGame }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Could not create room');
    session = { code: result.code, role: 'host', id: 'host', token: result.hostToken };
    writeSession(session);
    history.replaceState(null, '', `/?room=${result.code}&host=1`);
    connect();
  } catch (error) {
    showToast(error.message, true);
    button.disabled = false;
    button.textContent = 'Start a party';
  }
}

async function joinRoom(form) {
  const button = form.querySelector('button');
  button.disabled = true;
  try {
    await audio.unlock().catch(() => {});
    const room = form.dataset.room;
    const response = await fetch(`/api/rooms/${room}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: new FormData(form).get('name') }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Could not join room');
    session = { code: room, role: 'player', id: result.playerId, token: result.playerToken, name: result.name };
    writeSession(session);
    connect();
  } catch (error) {
    renderJoin(form.dataset.room, error.message);
  }
}

function syncTimer() {
  clearInterval(timerInterval);
  const tick = () => {
    if (!state?.deadline) return;
    const remaining = Math.max(0, state.deadline - Date.now());
    const total = phaseDuration(state.phase);
    const percent = Math.max(0, Math.min(100, (remaining / total) * 100));
    document.querySelectorAll('.timer__fill').forEach((element) => { element.style.width = `${percent}%`; });
    document.querySelectorAll('.timer__number').forEach((element) => { element.textContent = `${Math.ceil(remaining / 1000)}s`; });
  };
  tick();
  timerInterval = setInterval(tick, 250);
}

function phaseDuration(phase) {
  return {
    'signal-compose': 75_000, 'signal-guess': 45_000, 'story-write': 90_000, 'story-vote': 70_000,
    'vector-command': 70_000, 'crowd-place': 75_000, 'crowd-vote': 45_000,
    'collision-write': 55_000, 'collision-guess': 40_000, 'vibe-pick': 60_000,
    'bluff-write': 70_000, 'bluff-vote': 50_000, 'sketch-draw': 100_000,
    'sketch-caption': 60_000, 'sketch-vote': 45_000,
  }[phase] || 60_000;
}

function showToast(message, error = false) {
  toast.textContent = message;
  toast.className = `toast is-visible ${error ? 'is-error' : ''}`;
  setTimeout(() => { toast.className = 'toast'; }, 2600);
}

function showReaction(message) {
  const player = playerById(message.playerId);
  const node = document.createElement('div');
  node.className = 'floating-reaction';
  node.textContent = `${message.emoji} ${player?.name || ''}`;
  node.style.left = `${15 + Math.random() * 70}%`;
  document.body.append(node);
  setTimeout(() => node.remove(), 1900);
}

function playerById(id) {
  return state?.players?.find((player) => player.id === id) || null;
}

function joinUrl() {
  return `${location.origin}/?room=${state?.code || session?.code || ''}`;
}

function paintSketchCell(cell) {
  const index = Number(cell.dataset.cell);
  if (!Number.isInteger(index) || index < 0 || index >= 192) return;
  const cells = [...sketchDraft];
  cells[index] = sketchColor;
  sketchDraft = cells.join('');
  cell.className = `ink-${sketchColor}`;
}

function normalizeRoom(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6);
}

function sessionKey(code) {
  return `joybox-party-session:${code}`;
}

function readSession(code) {
  try {
    const value = JSON.parse(localStorage.getItem(sessionKey(code)));
    return value && value.code === code && ['host', 'player'].includes(value.role)
      && typeof value.token === 'string' && value.token && typeof value.id === 'string' ? value : null;
  } catch { return null; }
}

function writeSession(value) {
  try { localStorage.setItem(sessionKey(value.code), JSON.stringify(value)); }
  catch { showToast('Browser storage is unavailable. Keep this tab open to stay in the room.'); }
}

function clearSession(code) {
  try { localStorage.removeItem(sessionKey(code)); } catch { /* Play still works without storage. */ }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : 0;
}

function gameRules(id) {
  const rules = {
    'signal-jam': ['One player sees a secret scene, such as a robot at a birthday party.', 'That player picks three labeled icons and sounds. Preview them before sending.', 'Everyone else chooses the matching scene from four options. Correct guesses score for both sides.'],
    'story-circuit': ['Read the shared story setup and the secret direction on your phone.', 'Write one short continuation. Then match the other stories to their secret directions.', 'Vote for your favorite other story. Correct matches and votes earn points.'],
    'vector-vault': ['Read your thruster’s special rule on your phone. Tell your teammates what it does.', 'Together, choose each thruster’s direction and power to reach the target.', 'Lock your controls, watch the combined flight, and adjust your plan next round.'],
    'crowd-current': ['One forecaster predicts where the group will land on a two-axis opinion map.', 'Everyone else places their honest answer and adds a short caption.', 'Reveal the group’s center, then vote for another caption. Closest forecasts earn more points.'],
    'clue-collision': ['Everyone except the solver sees the same secret word.', 'Write one helpful word. Identical clues disappear, so try something specific.', 'The solver guesses from the remaining clues. Two players use a simpler one-clue version.'],
    'vibe-check': ['The hot-seat player picks their favorite option and their hard no.', 'Everyone else predicts those two choices on their phones.', 'Reveal the answers together. Correct predictions earn points, then the hot seat moves on.'],
    'bluff-bureau': ['Write a believable fake answer to an unusual trivia question.', 'The truth is mixed in with the room’s answers. Choose the one you think is real.', 'Earn points by finding the truth and by getting other players to choose your bluff.'],
    'sketch-switch': ['Draw your secret prompt on the phone’s pixel canvas.', 'Your drawing moves to someone else, who adds a caption without seeing the prompt.', 'Reveal the unexpected pairings. Three or more players vote; two players skip voting.'],
  };
  return `<aside class="rules-card"><span class="eyebrow">HOW TO PLAY ${GAMES[id].name}</span><ol>${rules[id].map((rule) => `<li>${escapeHtml(rule)}</li>`).join('')}</ol><p>Everyone gets the same number of spotlight turns. The host can end a phase if someone steps away.</p></aside>`;
}
